/**
 * useLikedVideos Hook
 *
 * Feature-specific hook for the Liked Videos page.
 *
 * Architecture: Page → Hook → UseCase → Service
 *
 * Mirrors Flutter's LikedContentScreenState
 * (lib/presentation/screens/content/liked_content_screen.dart).
 *
 * Responsibilities:
 * - Snapshot the locally-stored UserMediaLike records once on init via
 *   `likedVideosService.listLocalLikedVideos(currentUser)` and paginate
 *   against that snapshot, matching Flutter's `_getData`
 *   (liked_content_screen.dart:115-160).
 * - Listen reactively to secure-storage writes under the `LIKES` prefix
 *   (`UserMediaLike_`); unlike the unlocked-videos hook, likes can flip
 *   both ways, so the effect handles BOTH adds (`liked === true`) and
 *   removes (`liked === false`) — mirrors Flutter `_onBoxDataEvent`
 *   (liked_content_screen.dart:94-113).
 * - Surface loading / loading-more / hasMore / error to the UI.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useListenToSecureStorageUpdatesByPrefix } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UserMediaLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { RootState } from '@/presentation/store';
import { likedVideosService } from '@/domain/services/likedVideos/likedVideosService';
import { fetchLikedVideosPageUseCase } from '@/domain/usecases/likedVideos/fetchLikedVideosPageUseCase';
import { getUserMediaByPathUseCase } from '@/domain/usecases/userMedia/getUserMediaByPathUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export interface UseLikedVideosResult {
  medias: UserMediaEntity[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  init: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useLikedVideos = (): UseLikedVideosResult => {
  const { l10n } = useLocale();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Mirror `currentUser` into a ref so the live-update effect doesn't re-run
  // every time the auth listener mirrors SecureStorage into Redux (which
  // produces a fresh `UserEntity` reference even when nothing relevant
  // changed — see .claude/context/state-slices.md). The use cases inside the
  // live-update IIFE need the full object, so ref-mirroring is cleaner than
  // pulling out scalars.
  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  });

  // Pure live-update channel — `loadInitialItems: false` because `init()`
  // below loads the existing items explicitly through the service. The
  // hook's `items` starts empty and only grows / updates when a like is
  // toggled (either direction) while the page is open.
  const { items: liveLocalItems } =
    useListenToSecureStorageUpdatesByPrefix<UserMediaLikeStorage>(
      SECURE_STORAGE_KEYS.LIKES,
      (item) => item.path,
      false,
    );

  const [medias, setMedias] = useState<UserMediaEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The snapshot is taken once on init and is the basis for every page
  // fetch — matches Flutter's `_userMediaLikes`, which is read once
  // (synchronously, in `initState`) and re-sliced by `_getData`.
  const localItemsSnapshotRef = useRef<UserMediaLikeStorage[]>([]);
  const pageIndexRef = useRef(0);
  // Every path already covered by the pagination snapshot. With the live
  // hook in `loadInitialItems: false` mode, `liveLocalItems` only contains
  // post-mount writes — so this set is mainly defensive: it prevents the
  // live-update effect from prepending a duplicate if a write happens to
  // arrive for a path that was also in the snapshot (e.g. a race between
  // `listLocalLikedVideos` and `setEncrypted`).
  //
  // On an unlike event we REMOVE the path from this set so a subsequent
  // re-like is treated as a new add (Flutter's Hive box event fires on
  // every write regardless of whether the path was already seen — this
  // mirrors that behavior).
  const seenPathsRef = useRef<Set<string>>(new Set());
  // Guard so the live-update effect doesn't run before init has resolved
  // (otherwise initial paths would be treated as "new" and double-fetched).
  const hasInitializedRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  const init = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const snapshot = await likedVideosService.listLocalLikedVideos(currentUser);
      localItemsSnapshotRef.current = snapshot;
      pageIndexRef.current = 0;

      const result = await fetchLikedVideosPageUseCase.execute({
        localItems: snapshot,
        pageIndex: 0,
        currentUser,
      });

      // Seed with EVERY path in the snapshot — see the ref's declaration
      // for the rationale.
      const initialSeen = new Set<string>();
      for (const item of snapshot) {
        initialSeen.add(item.path);
      }
      seenPathsRef.current = initialSeen;

      setMedias(result.medias);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error fetching liked videos', 'useLikedVideos.init', { error: err as Error });
      setError(l10n.likedVideos.errorLoading);
      setMedias([]);
      setHasMore(false);
    } finally {
      // Set the ref before flipping isLoading: the live-update effect
      // gates on !isLoading and reads this ref, so the ref must be true
      // by the time the re-render from setIsLoading(false) fires it.
      hasInitializedRef.current = true;
      setIsLoading(false);
    }
  }, [currentUser, l10n]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (isLoadingMoreRef.current || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    setError(null);

    try {
      const nextIndex = pageIndexRef.current + 1;
      const result = await fetchLikedVideosPageUseCase.execute({
        localItems: localItemsSnapshotRef.current,
        pageIndex: nextIndex,
        currentUser,
      });

      pageIndexRef.current = nextIndex;
      setMedias((prev) => [...prev, ...result.medias]);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error loading more liked videos', 'useLikedVideos.loadMore', { error: err as Error });
      setError(l10n.likedVideos.loadMoreFailed);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, currentUser, l10n]);

  // Reactive add/remove for like toggles — mirrors Flutter
  // `_onBoxDataEvent` (liked_content_screen.dart:94-113). Unlike the
  // unlocked-videos hook, this effect handles BOTH directions:
  //  - `liked === false`: remove the corresponding media from the grid
  //    and drop the path from `seenPathsRef` so a re-like is treated
  //    as new.
  //  - `liked === true` + not yet seen + not hidden: resolve the media
  //    via `getUserMediaByPathUseCase` and prepend it.
  //  - `liked === true` + already seen: no-op (already rendered).
  //
  // `isLoading` is a dep (not just the ref) so the effect re-runs after
  // init finishes — covers the race where a live write lands between
  // `listLocalLikedVideos` resolving and `setIsLoading(false)`.
  useEffect(() => {
    if (!hasInitializedRef.current || isLoading) {
      return;
    }
    if (liveLocalItems.length === 0) {
      return;
    }

    // Partition the live items into removes and potential adds.
    const removes: UserMediaLikeStorage[] = [];
    const adds: UserMediaLikeStorage[] = [];
    for (const item of liveLocalItems) {
      if (!item.liked) {
        if (seenPathsRef.current.has(item.path)) {
          removes.push(item);
        }
      } else if (!seenPathsRef.current.has(item.path)) {
        adds.push(item);
      }
    }

    // Apply removes synchronously — no Firestore lookup needed.
    if (removes.length > 0) {
      const removedPaths = new Set(removes.map((item) => item.path));
      for (const path of removedPaths) {
        seenPathsRef.current.delete(path);
      }
      setMedias((prev) => prev.filter((media) => !removedPaths.has(media.relativePath)));
    }

    if (adds.length === 0) {
      return;
    }

    let cancelled = false;
    const hiddenContent = currentUserRef.current?.hiddenContent ?? [];

    (async () => {
      for (const item of adds) {
        // Skip items hidden by the current user (Flutter snapshot filter
        // at liked_content_screen.dart:84 — we apply the same predicate
        // here so live adds stay consistent with the initial snapshot).
        if (hiddenContent.includes(item.mediaId)) {
          // Mark as seen so we don't re-attempt on every subsequent event.
          seenPathsRef.current.add(item.path);
          continue;
        }

        try {
          const media = await getUserMediaByPathUseCase.execute(item.path, currentUserRef.current);
          if (cancelled) {
            return;
          }
          // Mark as seen even if filtered out by the blocked/hidden
          // guard inside `getUserMediaByPathUseCase`, to avoid re-fetching
          // it on every subsequent storage event.
          seenPathsRef.current.add(item.path);
          if (media) {
            setMedias((prev) => [media, ...prev]);
          }
        } catch (err) {
          appLogger.error(
            'Error resolving newly-liked media',
            'useLikedVideos.onLiveAdd',
            { error: err as Error, context: { path: item.path } },
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [liveLocalItems, isLoading]);

  return {
    medias,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    init,
    loadMore,
  };
};
